import type { CoverageLookups } from './coverageReport'
import type { OvertimeFilters, OvertimeGrouping, OvertimeRow, OvertimeTotal } from './overtimeFigures'
import type { ExportTable } from './reportExport'
import { supervisorLabel } from './supervisorLabel'

export type OvertimeView = 'entries' | OvertimeGrouping

export const OVERTIME_GROUP_LABEL: Record<OvertimeGrouping, string> = { employee: 'Employee', team: 'Team', period: 'Pay period' }

type Input = {
  view: OvertimeView
  from: string
  to: string
  filters: OvertimeFilters
  lookups: Pick<CoverageLookups, 'teams' | 'supervisors'>
  generatedAt: Date
}

function parameters(input: Input) {
  const { filters, lookups } = input
  return [
    { label: 'Date range', value: `${input.from} to ${input.to}` },
    { label: 'View', value: input.view === 'entries' ? 'Entries' : `Totals by ${OVERTIME_GROUP_LABEL[input.view].toLowerCase()}` },
    { label: 'Team', value: filters.teamId ? (lookups.teams.get(filters.teamId) ?? 'Unknown') : 'All' },
    { label: 'Supervisor', value: filters.supervisorId ? supervisorLabel(filters.supervisorId, lookups) : 'All' },
    { label: 'Shift', value: filters.shiftName ?? 'All' },
    { label: 'Assignment', value: filters.assignmentName ?? 'All' },
    { label: 'Detailed-out personnel', value: filters.includeDetailedOut ? 'Included' : 'Excluded' },
    { label: 'Employee search', value: filters.search ? filters.search : 'None' },
  ]
}

export function overtimeEntriesTable(rows: OvertimeRow[], hours: number, input: Input): ExportTable {
  return {
    title: 'Overtime Planning Report',
    parameters: parameters(input),
    generatedAt: input.generatedAt,
    columns: [
      { header: 'Employee', width: 22 },
      { header: 'Team' },
      { header: 'Date', width: 12 },
      { header: 'Shift' },
      { header: 'Assignment', width: 22 },
      { header: 'Planned hours', numeric: true, width: 14 },
      { header: 'Reason', width: 28 },
      { header: 'Coverage gap', width: 30 },
      { header: 'Detailed out' },
    ],
    rows: rows.map((row) => [
      row.employee,
      row.team,
      row.date,
      row.shift,
      row.assignment,
      row.hours,
      row.reason ?? 'None given',
      row.gap,
      row.detailedOut ? 'Yes' : 'No',
    ]),
    totals: { label: 'Total', values: [null, null, null, null, null, hours, null, null, null] },
  }
}

export function overtimeTotalsTable(groups: OvertimeTotal[], grouping: OvertimeGrouping, input: Input): ExportTable {
  return {
    title: 'Overtime Planning Report',
    parameters: parameters(input),
    generatedAt: input.generatedAt,
    columns: [
      { header: OVERTIME_GROUP_LABEL[grouping], width: 28 },
      { header: 'Entries', numeric: true },
      { header: 'Planned hours', numeric: true, width: 14 },
      { header: 'Hours against a coverage gap', numeric: true, width: 28 },
    ],
    rows: groups.map((group) => [group.label, group.entries, group.hours, group.gapHours]),
    totals: {
      label: 'Total',
      values: [
        null,
        groups.reduce((sum, group) => sum + group.entries, 0),
        groups.reduce((sum, group) => sum + group.hours, 0),
        groups.reduce((sum, group) => sum + group.gapHours, 0),
      ],
    },
  }
}
